import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Linkedin } from "lucide-react";

// Advisory Board Member Images
const ADVISOR_IMAGES = {
  neeta: "/assets/images/advisory/neeta.png",
  skand: "/assets/images/advisory/skand.png",
  revathi: "/assets/images/advisory/revathi.png",
  sanyogita: "/assets/images/advisory/sanyogita.png",
  vidhukesh: "/assets/images/advisory/vidhukesh.png",
  jaya: "/assets/images/advisory/jaya.png",
  ashish: "/assets/images/advisory/ashish.png"
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const AdvisoryPage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef(null);

  const advisors = [
    {
      name: "Dr. Neeta Bali",
      role: "Director Academics",
      org: "Modern Vidya Niketan Schools, Faridabad",
      bio: "Known for her ability to lead and successfully turn around institutions. Experience spans CBSE, ISC, ICSE, IGCSE, IB-DP, and AS & A Levels. Led Kasiga School to top ten co-educational residential schools in India and to No. 1 in Uttarakhand and Dehradun within less than two years.",
      image: ADVISOR_IMAGES.neeta,
      linkedin: "https://www.linkedin.com/in/dr-neeta-bali-66103111/"
    },
    {
      name: "Dr. Skand Bali",
      role: "Principal",
      org: "The Hyderabad Public School, Begumpet",
      bio: "Dedicated to education since the turn of the millennium. Distinguished leadership journey includes GD Goenka World School, The Doon School, and Army Public School. Strong advocate of student-centric, values-driven education supported by transparent and collaborative practices.",
      image: ADVISOR_IMAGES.skand,
      linkedin: "https://www.linkedin.com/in/skand-bali-482ab3126/"
    },
    {
      name: "Dr. Revathi Srinivasan",
      role: "Director & Group Dean",
      org: "Singhania Group of Schools",
      bio: "Visionary leader with over three decades of experience. Recipient of Global Icon Award for Leadership in Education at House of Lords, British Parliament. Pioneered curriculum resources showcased at IIM Ahmedabad, while also leading rural immersion programs.",
      image: ADVISOR_IMAGES.revathi,
      linkedin: "https://www.linkedin.com/in/revathis/"
    },
    {
      name: "Ms. Sanyogita Sharma",
      role: "Director",
      org: "Manav Rachna International Schools",
      bio: "Over 35 years of experience in school education. Played pivotal role in establishing eight schools across NCR and Punjab. Currently driving innovation and growth as Director of the Manav Rachna International Schools chain.",
      image: ADVISOR_IMAGES.sanyogita,
      linkedin: "https://www.linkedin.com/in/sanyogita-sharma-5099041a9/"
    },
    {
      name: "Dr. Vidhukesh Vimal",
      role: "Principal",
      org: "Pestle Weed School, Dehradun",
      bio: "Ph.D. and M.Phil. from University of Delhi, International Certificate from UCL, Certificate from Harvard Business School. Former House Master of Jaipur House at The Doon School. Passionate about literature, photography, and theatre.",
      image: ADVISOR_IMAGES.vidhukesh,
      linkedin: "https://www.linkedin.com/in/dr-vidhukesh-vimal-bb034339/"
    },
    {
      name: "Dr. Jaya Parekh",
      role: "Principal",
      org: "DPS Hinjawadi, Pune",
      bio: "Over 25 years in education including 15 years in school leadership. Holds PhD in Chemistry and MBA in Education Management. Known for strategic vision, academic excellence, and people-centric leadership.",
      image: ADVISOR_IMAGES.jaya,
      linkedin: "https://www.linkedin.com/in/dr-jaya-parekh-3b0a25283/"
    },
    {
      name: "Mr. Ashish Bhatnagar",
      role: "Principal",
      org: "Ram Ratna International School, Mumbai",
      bio: "Expert in AI in Education, actively trains school leaders and teachers on AI integration. Extensive experience with Cambridge K-12 and IB programmes including EY and PYP. Committed to future-ready education and technology-driven transformation.",
      image: ADVISOR_IMAGES.ashish,
      linkedin: "https://www.linkedin.com/in/ashish-bhatnagar-6695013/"
    }
  ];

  // Auto-scroll carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % advisors.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [advisors.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + advisors.length) % advisors.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % advisors.length);
  };

  // Get visible advisors (3 at a time on desktop)
  const getVisibleAdvisors = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      visible.push(advisors[(currentIndex + i) % advisors.length]);
    }
    return visible;
  };

  return (
    <div className="pt-24">
      {/* Hero */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-blue-50 via-white to-yellow-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto text-center">
            <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Leadership</span>
            <h1 className="font-heading text-4xl md:text-6xl font-semibold mt-4 mb-6 text-slate-800">
              Advisory <span className="gradient-text">Board</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Our advisory board comprises distinguished education leaders who guide GSLC's vision 
              and help shape the future of school leadership in India.
            </p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Advisory Board Members - Scrolling Carousel */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          {/* Desktop Carousel */}
          <motion.div variants={fadeInUp} className="hidden md:block relative">
            <div className="flex gap-8 justify-center">
              {getVisibleAdvisors().map((advisor, index) => (
                <div
                  key={`${currentIndex}-${index}`}
                  className="card-light rounded-3xl p-8 hover-lift w-[350px] flex-shrink-0"
                >
                  {/* Big Image */}
                  <div className="w-40 h-40 rounded-2xl overflow-hidden mb-6 mx-auto shadow-xl">
                    <img 
                      src={advisor.image} 
                      alt={advisor.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="text-center">
                    <h3 className="font-heading text-xl text-slate-800 mb-1">{advisor.name}</h3>
                    <p className="text-red-600 text-sm font-medium mb-1">{advisor.role}</p>
                    <p className="text-slate-400 text-sm mb-4">{advisor.org}</p>
                    
                    {/* LinkedIn Icon */}
                    <a 
                      href={advisor.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors mb-4"
                    >
                      <Linkedin size={18} />
                    </a>
                  </div>
                  
                  <p className="text-slate-500 text-sm leading-relaxed text-center line-clamp-4">{advisor.bio}</p>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={goToPrevious}
              className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-slate-800 hover:bg-yellow-400 transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-slate-800 hover:bg-yellow-400 transition-colors"
            >
              <ChevronRight size={24} />
            </button>
          </motion.div>

          {/* Mobile: Single Card Carousel */}
          <motion.div variants={fadeInUp} className="md:hidden">
            <div className="card-light rounded-3xl p-8">
              {/* Big Image */}
              <div className="w-48 h-48 rounded-2xl overflow-hidden mb-6 mx-auto shadow-xl">
                <img 
                  src={advisors[currentIndex].image} 
                  alt={advisors[currentIndex].name}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="text-center">
                <h3 className="font-heading text-xl text-slate-800 mb-1">{advisors[currentIndex].name}</h3>
                <p className="text-red-600 text-sm font-medium mb-1">{advisors[currentIndex].role}</p>
                <p className="text-slate-400 text-sm mb-4">{advisors[currentIndex].org}</p>
                
                {/* LinkedIn Icon */}
                <a 
                  href={advisors[currentIndex].linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors mb-4"
                >
                  <Linkedin size={18} />
                </a>
              </div>
              
              <p className="text-slate-500 text-sm leading-relaxed text-center">{advisors[currentIndex].bio}</p>
            </div>

            {/* Mobile Navigation */}
            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={goToPrevious}
                className="w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center text-slate-800"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={goToNext}
                className="w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center text-slate-800"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </motion.div>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {advisors.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-gradient-to-r from-yellow-500 via-red-600 to-blue-800 w-8' 
                    : 'bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* CTA - Changed from "Join GSLC" */}
      <AnimatedSection className="section-padding bg-gradient-to-r from-slate-50 via-white to-slate-50">
        <div className="container-custom">
          <motion.div 
            variants={fadeInUp}
            className="bg-gradient-to-r from-yellow-500 via-red-600 to-blue-800 rounded-3xl p-12 text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
              Learn from the Best
            </h2>
            <p className="text-white/90 mb-8 max-w-xl mx-auto">
              Connect with our advisory board and gain insights from India's top education leaders.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-white text-blue-900 font-semibold hover:bg-yellow-400 transition-colors">
              Get In Touch <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default AdvisoryPage;
